import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getAccountById, getPrimaryUserIdForAccount } from '../../services/firestoreService';
import { getMarketingFulfillmentData, updateMarketingFulfillmentCategory, DEFAULT_CATEGORY_SHAPES } from '../../services/marketingFulfillmentDataService';
import { collection, query, where, orderBy, limit, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaArrowLeft, FaKey, FaImages, FaTrash } from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const MarketingFulfillmentWebsiteClient = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [account, setAccount] = useState(null);
  const [fulfillmentData, setFulfillmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('assets');
  const [status, setStatus] = useState('Not Started');
  const [savingStatus, setSavingStatus] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [deletingWebsite, setDeletingWebsite] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const acct = await getAccountById(clientId);
        if (!acct || cancelled) return;
        setAccount(acct);

        // Load website request(s) for this account to drive status.
        // Avoid composite index by NOT ordering in Firestore; sort in JS instead.
        const requestsRef = collection(db, 'marketingRequests');
        const reqQuery = query(
          requestsRef,
          where('category', '==', 'website'),
          where('accountId', '==', clientId)
        );
        const reqSnap = await getDocs(reqQuery);
        if (!cancelled && !reqSnap.empty) {
          const docs = reqSnap.docs.slice();
          docs.sort((a, b) => {
            const ad = a.data();
            const bd = b.data();
            const aTime = ad.createdAt?.toDate ? ad.createdAt.toDate().getTime() : (ad.createdAt ? new Date(ad.createdAt).getTime() : 0);
            const bTime = bd.createdAt?.toDate ? bd.createdAt.toDate().getTime() : (bd.createdAt ? new Date(bd.createdAt).getTime() : 0);
            return bTime - aTime;
          });
          const latest = docs[0];
          const latestData = latest.data();
          setRequestId(latest.id);
          setStatus(latestData.websiteStatus || 'Not Started');
        }

        // Website fulfillment is stored at the company/account level,
        // keyed by the account (client) ID used by the portal.
        const data = await getMarketingFulfillmentData(clientId);
        if (!cancelled) setFulfillmentData(data);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading website client data:', err);
          setError(err.message || 'Failed to load data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientId]);

  const website = fulfillmentData?.website || {};
  const imageVideoAssets = Array.isArray(website.imageVideoAssets) ? website.imageVideoAssets : [];
  const logoFiles = Array.isArray(website.logoFiles) ? website.logoFiles : [];

  const handleDeleteWebsiteFulfillment = async () => {
    if (!clientId || deletingWebsite) return;
    const confirmed = window.confirm("Are you sure you want to clear this client's website fulfillment data?");
    if (!confirmed) return;
    try {
      setDeletingWebsite(true);
      const emptyWebsite = { ...DEFAULT_CATEGORY_SHAPES.website };
      await updateMarketingFulfillmentCategory(clientId, 'website', emptyWebsite);
      setFulfillmentData((prev) => ({
        ...(prev || {}),
        website: emptyWebsite
      }));
    } catch (err) {
      console.error('Error clearing website fulfillment data:', err);
      alert('Failed to clear website fulfillment data. Please try again.');
    } finally {
      setDeletingWebsite(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!requestId) return;
    try {
      setSavingStatus(true);
      const requestRef = doc(db, 'marketingRequests', requestId);
      await updateDoc(requestRef, {
        websiteStatus: status,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating website status:', err);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading client data...</p>
        </div>
      </div>
    );
  }

  if (error || !clientId) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative rounded-2xl p-6 max-w-md bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <p className="text-red-600 dark:text-red-400">{error || 'Client not found.'}</p>
          <button
            type="button"
            onClick={() => navigate('/crm/fulfillment/marketing')}
            className={`mt-4 px-4 py-2 rounded-md ${darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
          >
            Back to Marketing Fulfillment
          </button>
        </div>
      </div>
    );
  }

  const companyName = account?.company || 'Client';

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-center relative">
            <button
              type="button"
              onClick={() => navigate('/crm/fulfillment/marketing')}
              className={`absolute left-0 flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Back to Marketing Fulfillment"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-1">
                Website – {companyName}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Access &amp; Logins and Photo &amp; Video Assets
              </p>
            </div>
            <button
              type="button"
              onClick={handleDeleteWebsiteFulfillment}
              disabled={deletingWebsite}
              className={`absolute right-0 flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-red-900 hover:text-white' : 'text-gray-600 hover:bg-red-50 hover:text-red-700'
              } disabled:opacity-50`}
              title="Clear website fulfillment data"
            >
              <FaTrash className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status section */}
        <div className={`mb-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
          <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <span className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</span>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Track this client's website fulfillment progress.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={`px-3 py-2 rounded-md text-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="Not Started">Not Started</option>
                <option value="Requested">Requested</option>
                <option value="In Progress">In Progress</option>
                <option value="Live">Live</option>
              </select>
              <button
                type="button"
                onClick={handleSaveStatus}
                disabled={savingStatus || !requestId}
                className="px-4 py-2 rounded-md bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {savingStatus ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex justify-center">
          <div className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm inline-flex p-1`}>
            <button
              type="button"
              onClick={() => setActiveTab('assets')}
              className={`px-4 py-2 text-sm rounded-md ${
                activeTab === 'assets'
                  ? 'bg-orange-500 text-white'
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Photo &amp; Video Assets
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('access')}
              className={`ml-1 px-4 py-2 text-sm rounded-md ${
                activeTab === 'access'
                  ? 'bg-orange-500 text-white'
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Access &amp; Logins
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Access & Logins */}
          {activeTab === 'access' && (
          <section className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
            <div className={`px-6 py-4 border-b flex items-center gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <FaKey className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Access &amp; Logins</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Item label="WordPress admin sent" value={website.wordpressAdminSent ? 'Yes' : 'No'} darkMode={darkMode} />
                <Item label="Not built on WordPress" value={website.wordpressNotBuiltOn ? (website.wordpressNotBuiltOnPlatform || 'Yes') : 'No'} darkMode={darkMode} />
                <Item label="Hosting (GoDaddy)" value={website.hostingIsGodaddy ? 'Yes' : 'No'} darkMode={darkMode} />
                <Item label="Hosting access sent" value={website.hostingAccessSent ? 'Yes' : 'No'} darkMode={darkMode} />
                <Item label="Hosting provider" value={website.hostingAccess} darkMode={darkMode} />
                <Item label="Hosting username" value={website.hostingUsername} darkMode={darkMode} />
                <Item label="Business email" value={website.businessEmail} darkMode={darkMode} />
                <Item label="Business phone" value={website.businessPhone} darkMode={darkMode} />
                <Item label="Business address" value={website.businessAddress} darkMode={darkMode} className="sm:col-span-2" />
                <Item label="Brand fonts" value={website.brandFonts} darkMode={darkMode} />
                <Item label="Brand colors" value={website.brandColors} darkMode={darkMode} />
                <Item label="Hours of operation" value={website.hoursOfOperation} darkMode={darkMode} className="sm:col-span-2" />
                <Item label="Google Business Profile access sent" value={website.googleBusinessProfileAccessSent ? 'Yes' : 'No'} darkMode={darkMode} />
                <Item label="LinkedIn username" value={website.linkedInUsername} darkMode={darkMode} />
              </div>
              <div className="mt-4 space-y-3">
                <Item label="Additional brand insights" value={website.additionalBrandInsights} darkMode={darkMode} />
                <Item
                  label="Services list"
                  value={Array.isArray(website.servicesList) && website.servicesList.length > 0 ? website.servicesList.join(', ') : ''}
                  darkMode={darkMode}
                />
                <Item
                  label="Service areas"
                  value={Array.isArray(website.serviceAreas) && website.serviceAreas.length > 0 ? website.serviceAreas.join(', ') : ''}
                  darkMode={darkMode}
                />
              </div>
            </div>
          </section>
          )}

          {/* Photo & Video Assets */}
          {activeTab === 'assets' && (
          <section className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
            <div className={`px-6 py-4 border-b flex items-center gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <FaImages className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Photo &amp; Video Assets</h2>
            </div>
            <div className="p-6 space-y-6">
              {imageVideoAssets.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Project photos, team photos, branding, videos, logos</h3>
                  <ul className="flex flex-wrap gap-2">
                    {imageVideoAssets.map((item, i) => {
                      const url = typeof item === 'object' && item?.url ? item.url : (typeof item === 'string' && item.startsWith('http') ? item : null);
                      const name = typeof item === 'object' && item?.name ? item.name : (url ? 'Link' : String(item));
                      return (
                        <li key={i}>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-orange-600 dark:text-orange-400 hover:underline text-sm">
                              {name}
                            </a>
                          ) : (
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{name}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {logoFiles.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Logo files</h3>
                  <ul className="flex flex-wrap gap-2">
                    {logoFiles.map((item, i) => {
                      const url = typeof item === 'object' && item?.url ? item.url : (typeof item === 'string' && item.startsWith('http') ? item : null);
                      const name = typeof item === 'object' && item?.name ? item.name : (url ? 'Logo' : String(item));
                      return (
                        <li key={i}>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-orange-600 dark:text-orange-400 hover:underline text-sm">
                              {name}
                            </a>
                          ) : (
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{name}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {imageVideoAssets.length === 0 && logoFiles.length === 0 && (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No photo or video assets uploaded yet.</p>
              )}
            </div>
          </section>
          )}
        </div>
      </div>
    </div>
  );
};

function Item({ label, value, darkMode, className = '' }) {
  return (
    <div className={className}>
      <span className={`block text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</span>
      <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>{value === undefined || value === null || value === '' ? '—' : String(value)}</p>
    </div>
  );
}

export default MarketingFulfillmentWebsiteClient;
